/**
 * Thin fetch wrapper for the A5Core API.
 * Base URL is configurable via VITE_API_URL env var, defaults to /api.
 *
 * AUTHENTICATION MODEL:
 * - The JWT lives in an httpOnly cookie set by the server at login. It is NOT
 *   readable by JavaScript and is never persisted to localStorage — an XSS
 *   cannot steal a persistent token. All requests send it automatically
 *   (same-origin, plus credentials: 'include').
 * - After login we ALSO keep the token returned in the response body in memory
 *   for the lifetime of the page (AiBridge calls and explicit Bearer-header
 *   fetches use it). On reload it is gone; the cookie re-authenticates.
 * - localStorage holds only a non-sensitive session hint (role + expiry) used
 *   for fast route gating. The API remains the authority — a 401 clears it.
 */

export const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';

const SESSION_KEY = 'a5core_session';
const WORKSPACE_ID_KEY = 'a5core_workspace_id';

export class ApiError extends Error {
  status: number;
  statusText: string;
  body: string;

  constructor(status: number, statusText: string, body: string) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

// Session state

interface SessionHint {
  role: string;
  expiresAt: string;
}

/** In-memory token from the login response — gone on page reload (by design). */
let memoryToken: string | null = null;

function readSessionHint(): SessionHint | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const hint = JSON.parse(raw) as SessionHint;
    if (!hint || typeof hint.expiresAt !== 'string') return null;
    return hint;
  } catch {
    return null;
  }
}

function writeSessionHint(hint: SessionHint): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(hint));
}

function clearSessionHint(): void {
  localStorage.removeItem(SESSION_KEY);
}

// Token management (compatibility surface — memory only, never persisted)

export function getToken(): string | null {
  return memoryToken;
}

export function setToken(token: string): void {
  memoryToken = token;
}

export function clearToken(): void {
  memoryToken = null;
  clearSessionHint();
}

/** Current user's role from the session hint ('Admin', 'Accountant', 'Viewer', …), or null. */
export function getRole(): string | null {
  return readSessionHint()?.role ?? null;
}

export function isAdmin(): boolean {
  return getRole()?.toLowerCase() === 'admin';
}

export function isAuthenticated(): boolean {
  const hint = readSessionHint();
  if (!hint) return false;
  // Local expiry check only — a stale hint gets corrected by the next API 401.
  return Date.now() < new Date(hint.expiresAt).getTime();
}

/**
 * Boot-time session restore for the SSO bridge: the bridge lands on "/" via a
 * full-page redirect with the JWT in the httpOnly cookie — no JS ran to write
 * the localStorage session hint, so isAuthenticated() alone would false-negative
 * and re-render Login (the "login button doesn't work" loop). Probe /auth/me
 * (cookie-authenticated): 200 → write the hint and return true.
 */
export async function restoreSession(): Promise<boolean> {
  if (isAuthenticated()) return true;
  try {
    const response = await fetch(`${BASE_URL}/auth/me`, { credentials: 'include' });
    if (!response.ok) return false;
    const me = await response.json() as { username?: string; role?: string; expiresAt?: string };
    if (!me?.username || !me?.expiresAt) return false;
    writeSessionHint({ role: me.role ?? '', expiresAt: me.expiresAt });
    return true;
  } catch {
    return false;
  }
}

// Auth API

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  role: string;
}

export async function login(request: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText, body);
  }

  const data = await response.json() as LoginResponse;
  // Keep the token in memory only; the httpOnly cookie is the persistent credential.
  setToken(data.token);
  writeSessionHint({ role: data.role, expiresAt: data.expiresAt });
  return data;
}

export function logout(): void {
  // Best-effort server-side cookie clear (fire-and-forget).
  fetch(`${BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include', keepalive: true })
    .catch(() => { /* the local clear below is authoritative for the UI */ });
  clearToken();
  // Drop the workspace context too: a fresh login on another account must not
  // inherit it (and a deleted workspace's id must not haunt the next session —
  // the Stašaičio trap, 2026-09-11).
  localStorage.removeItem('a5core_workspace_id');
}

// Core request helper

/**
 * Paths that are NOT workspace-scoped and must not carry X-Workspace-Id (a
 * stale stored id would 404 them). Shared by request() and downloadFile()
 * so the two can never drift apart.
 */
function shouldSkipWorkspaceHeader(path: string): boolean {
  return (
    path.startsWith('/workspaces') || path.startsWith('/auth') ||
    path.startsWith('/config') || path.startsWith('/findings')
  );
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  retriedAfterStaleWorkspace = false,
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  // Bearer header when we hold an in-memory token (post-login, same page);
  // otherwise the httpOnly cookie authenticates (set for every request below).
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Attach workspace ID if available (skip for workspace/auth/config/findings
  // endpoints — they are not workspace-scoped and must not 404 on a stale id;
  // findings is personal/global, owner rule 2026-09-13)
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
  if (workspaceId && !shouldSkipWorkspaceHeader(path)) {
    headers['X-Workspace-Id'] = workspaceId;
  }

  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, { ...options, headers, credentials: 'include' });

  if (!response.ok) {
    if (response.status === 401) {
      clearToken();
    }
    const body = await response.text().catch(() => '');
    // Self-heal: a stored workspace id that no longer exists server-side
    // (workspace deleted, account switched) 404s every workspace-scoped call
    // and wedges the app. Drop the stale id and retry once without it.
    if (
      response.status === 404 && workspaceId && !retriedAfterStaleWorkspace &&
      body.includes('Workspace') && body.includes('not found')
    ) {
      localStorage.removeItem(WORKSPACE_ID_KEY);
      return request<T>(path, options, true);
    }
    throw new ApiError(response.status, response.statusText, body);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) => {
    const searchParams = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
          searchParams.set(key, String(value));
        }
      }
    }
    const query = searchParams.toString();
    return request<T>(query ? `${path}?${query}` : path);
  },

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
};

// File download helper

/**
 * Best-effort filename from a Content-Disposition header
 * (`attachment; filename="Saskaita-123.pdf"` or the RFC 5987 `filename*=`
 * form), or null when absent/unparseable.
 */
function parseContentDispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through to the plain form
    }
  }
  const plainMatch = /filename="?([^";]+)"?/.exec(header);
  return plainMatch?.[1] ?? null;
}

/**
 * Download a binary file (e.g. an invoice PDF) through fetch and hand it to
 * the browser via an object URL.
 *
 * WHY this exists instead of a plain `<a href="/api/...">` (finding 46):
 * workspace-scoped endpoints resolve the tenant from the X-Workspace-Id
 * REQUEST HEADER. A browser navigation fires no such header, so the API's
 * workspace middleware resolves the ACTIVE workspace — not the user's
 * selected one — and the user gets an error page instead of the file
 * ("sistema negeneruoja jokio PDF"). fetch() lets us attach exactly the same
 * auth + workspace context as request(); the blob is then saved locally
 * through a programmatic download anchor. Do not "simplify" this back to an
 * anchor — it will look fine in dev (single workspace) and break in prod.
 *
 * `path` is relative to BASE_URL, like every other call here.
 */
export async function downloadFile(path: string, fallbackFileName: string): Promise<void> {
  const headers: Record<string, string> = {};

  // Same auth context as request(): Bearer when we hold an in-memory token,
  // otherwise the httpOnly cookie authenticates (credentials: 'include').
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
  if (workspaceId && !shouldSkipWorkspaceHeader(path)) {
    headers['X-Workspace-Id'] = workspaceId;
  }

  const response = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });

  if (!response.ok) {
    if (response.status === 401) {
      clearToken();
    }
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText, body);
  }

  const blob = await response.blob();
  const fileName = parseContentDispositionFileName(response.headers.get('Content-Disposition')) ?? fallbackFileName;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick — the synchronous click has already started the
  // download, but revoking in the same tick can race slower browsers.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * POST for a binary blob (e.g. the invoice-template preview PDF) and return an
 * object URL for opening in a tab. Same auth + workspace context as request().
 * Caller decides when to revoke the URL.
 */
export async function postForBlobUrl(path: string): Promise<string> {
  return blobUrl(path, 'POST');
}

/** GET variant (e.g. the stored template logo image). */
export async function getBlobUrl(path: string): Promise<string> {
  return blobUrl(path, 'GET');
}

async function blobUrl(path: string, method: 'GET' | 'POST'): Promise<string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
  if (workspaceId && !shouldSkipWorkspaceHeader(path)) {
    headers['X-Workspace-Id'] = workspaceId;
  }
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 401) {
      clearToken();
    }
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText, body);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
